import { IconCoins, IconSparkles } from "@tabler/icons-react"
import { type FC, useState } from "react"
import { useSearchParams } from "react-router"

import { type AiCreditPackId, aiCreditPacks } from "@forento/core/models/ai"
import { toPriceString } from "@forento/core/utilities/currency"
import { toNumberFormat } from "@forento/core/utilities/number"

import AiCreditBalanceWarning from "~/components/AiCreditBalanceWarning"
import AiGenerationDiscountNotice from "~/components/AiGenerationDiscountNotice"
import Button from "~/components/Button"
import { usePlatform } from "~/contexts/UserContext"
import { captureForentoAiEvent, getBuyAiCreditsSource, useForentoAiImpression } from "~/utilities/forentoAiAnalytics"
import trpc from "~/utilities/trpc"

const AiCredits: FC = () => {
	const platform = usePlatform()!
	const [searchParams] = useSearchParams()

	const [submittingPackId, setSubmittingPackId] = useState<AiCreditPackId | null>(null)
	const [error, setError] = useState<string | null>(null)

	const purchaseCompleted = searchParams.get("ai-credits") === "success"
	const source = getBuyAiCreditsSource(searchParams)
	const isVisible = platform.enabledFeatures.includes("ai-generated-courses")

	useForentoAiImpression(
		"forento_ai_credit_packs_viewed",
		{ source, credit_balance: platform.aiCreditBalance },
		isVisible && !purchaseCompleted,
	)

	const handlePurchase = async (packId: AiCreditPackId) => {
		setSubmittingPackId(packId)
		setError(null)
		captureForentoAiEvent("forento_ai_credit_checkout_started", { pack_id: packId, source })
		try {
			const { redirectUrl } = await trpc.payment.creator.createAiCreditCheckout.mutate({ packId, source })
			window.location.assign(redirectUrl)
		} catch (error) {
			console.error(error)
			setError("Failed to start checkout. Please try again.")
			setSubmittingPackId(null)
		}
	}

	if (!isVisible) return null

	return (
		<section className="border-component-border bg-component-background mb-6 flex flex-col gap-4 rounded-3xl border p-6">
			<div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
				<div className="flex items-start gap-3">
					<div className="bg-primary/10 text-primary flex size-11 shrink-0 items-center justify-center rounded-lg">
						<IconSparkles className="size-5" />
					</div>
					<div className="flex flex-col gap-1">
						<h2 className="text-lg font-semibold text-gray-950">AI credits</h2>
						<p className="max-w-2xl text-sm leading-6 text-gray-600">
							Use credits to generate courses, chapters, blog posts and landing pages with Forento AI, and
							to edit courses and your website with the chat.
						</p>
					</div>
				</div>
				<div className="rounded-lg bg-gray-50 px-4 py-3">
					<div className="text-xs text-gray-500">Current balance</div>
					<div className="text-xl font-semibold text-gray-950">
						{toNumberFormat(platform.aiCreditBalance)} credits
					</div>
				</div>
			</div>

			{!purchaseCompleted && <AiCreditBalanceWarning />}
			<AiGenerationDiscountNotice />

			{purchaseCompleted && (
				<div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
					Your AI credits purchase is being added to your balance.
				</div>
			)}
			{error !== null && (
				<div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
			)}

			<div className="grid gap-3 lg:grid-cols-3">
				{aiCreditPacks.map(pack => (
					<div key={pack.id} className="flex flex-col gap-4 rounded-lg border border-gray-200 p-4">
						<div className="flex flex-col gap-1">
							<div className="flex items-center gap-2">
								<IconCoins className="text-primary size-5" />
								<h3 className="font-semibold text-gray-950">{pack.title}</h3>
							</div>
							<p className="text-sm leading-6 text-gray-600">{pack.description}</p>
						</div>
						<div className="mt-auto flex flex-col gap-3">
							<div>
								<div className="text-2xl font-semibold text-gray-950">
									{toNumberFormat(pack.credits)} credits
								</div>
								<div className="text-sm text-gray-500">{toPriceString(pack.price)}</div>
							</div>
							<Button
								variant="primary"
								onClick={() => handlePurchase(pack.id)}
								isLoading={submittingPackId === pack.id}
								isDisabled={submittingPackId !== null}
								className="w-full"
							>
								Buy credits
							</Button>
						</div>
					</div>
				))}
			</div>
		</section>
	)
}

export default AiCredits
